import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { FieldsSection } from '../../../../../../shared/components/form/layout/fieldsSection.component';
import {
  FieldsRow,
  FormCheckbox,
  FormImagePicker,
  FormNumericField,
  FormSelect,
  FormTextField,
} from '../../../../../../shared/components/form';
import {
  C_Bakery2B_Currency,
  C_Bakery2B_Language,
  C_Bakery2B_Menu_Color,
  C_Correspondence_Language,
} from '../../../../../../graphql/generatedModel';
import {
  FranceFlagIcon,
  GermanyFlagIcon,
  ItalyFlagIcon,
  UKFlagIcon,
} from '../../../../../../svg/custom.svg';
import { Stack } from '@mui/material';
import { bakery2bSettingsTabState } from '../../../states/bakery2bSettings.state';
import { createValidationSchema } from './validation/settings.schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { SaveBakery2bSettingsInput } from '../../../services/bakery2b.service';

const validationSchema = createValidationSchema();

export const SettingsTab: FC<ISettingsTab> = ({ params }) => {
  const { t } = useTranslation();
  const formMethods = useForm({
    defaultValues: params,
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });
  const {
    resetField,
    formState: { errors },
  } = formMethods;

  useEffect(() => {
    const formStateSub = formMethods.watch((_, reason) => {
      if (reason.type === 'change' && reason.name) {
        const fieldName = reason.name;
        const value = formMethods.getValues(fieldName);
        bakery2bSettingsTabState.pub.recordData(
          setByPath(fieldName, value) as SaveBakery2bSettingsInput,
        );
      }
    });

    const bakery2bSettingsTabStateSub = bakery2bSettingsTabState.sub
      .state()
      .subscribe(({ dataToSave }) => {
        const fieldPath = getSingleChangedPath(dataToSave);

        if (!fieldPath) {
          return;
        }

        resetField(fieldPath as any, {
          defaultValue: getValueByPath(dataToSave, fieldPath),
          keepDirty: false,
        });
      });

    return () => {
      formStateSub.unsubscribe();
      bakery2bSettingsTabStateSub.unsubscribe();
    };
  }, []);

  const languageOptions = useMemo(
    () => [
      {
        id: C_Bakery2B_Language.en_GB,
        label: t(`enums.${C_Correspondence_Language.CL1_ENGLISH}`),
        img: <UKFlagIcon />,
      },
      {
        id: C_Bakery2B_Language.de_DE,
        label: t(`enums.${C_Correspondence_Language.CL2_GERMAN}`),
        img: <GermanyFlagIcon />,
      },
      {
        id: C_Bakery2B_Language.fr_FR,
        label: t(`enums.${C_Correspondence_Language.CL3_FRENCH}`),
        img: <FranceFlagIcon />,
      },
      {
        id: C_Bakery2B_Language.it_IT,
        label: t(`enums.${C_Correspondence_Language.CL4_ITALIAN}`),
        img: <ItalyFlagIcon />,
      },
    ],
    [t],
  );

  const currencyOptions = useMemo(
    () => [
      {
        id: C_Bakery2B_Currency.CHF,
        label: t(`enums.${C_Bakery2B_Currency.CHF}`),
      },
      {
        id: C_Bakery2B_Currency.EUR,
        label: t(`enums.${C_Bakery2B_Currency.EUR}`),
      },
      {
        id: C_Bakery2B_Currency.USD,
        label: t(`enums.${C_Bakery2B_Currency.USD}`),
      },
    ],
    [t],
  );

  const menuBgColorOptions = useMemo(
    () => [
      {
        id: C_Bakery2B_Menu_Color.BMC_WHITE,
        label: t(`enums.${C_Bakery2B_Menu_Color.BMC_WHITE}`),
      },
      {
        id: C_Bakery2B_Menu_Color.BMC_RED,
        label: t(`enums.${C_Bakery2B_Menu_Color.BMC_RED}`),
      },
      {
        id: C_Bakery2B_Menu_Color.BMC_BLACK,
        label: t(`enums.${C_Bakery2B_Menu_Color.BMC_BLACK}`),
      },
    ],
    [t],
  );

  const onSubmit = (e: React.FocusEvent<HTMLFormElement>) => {
    const fieldName = e.target.name;
    const fieldState = formMethods.getFieldState(fieldName);

    if (!fieldState.invalid && fieldState.isDirty) {
      bakery2bSettingsTabState.pub.saveData();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form noValidate onBlur={onSubmit}>
        <FieldsSection titleBoxSx={{ pt: 0 }} nomt title={t('common.company_settings')}>
          <FieldsRow>
            <FormTextField
              name='companyName'
              label={t('settings.name')}
              width='270px'
              required
              error={!!errors.companyName}
              helperText={errors.companyName ? t(String(errors.companyName.message)) : ''}
            />
            <FormSelect
              name='language'
              options={languageOptions}
              label={t('common.language')}
              width='270px'
              disableClearable
              withImage
            />
          </FieldsRow>
          <FieldsRow>
            <FormSelect
              name='mainCurrency'
              options={currencyOptions}
              label={t('common.currency')}
              width='270px'
              disableClearable
            />
          </FieldsRow>
        </FieldsSection>
        <FieldsSection title={t('order.order_settings')}>
          <FieldsRow>
            <FormNumericField
              name='orderFutureDays'
              label={t('settings.available_future_days')}
              precision={0}
              noTrailingZeros
              controls
              width='270px'
              error={!!errors.orderFutureDays}
              helperText={errors.orderFutureDays ? t(String(errors.orderFutureDays.message)) : ''}
            />
            <FormNumericField
              name='orderMinimalValue'
              precision={0}
              noTrailingZeros
              controls
              width='270px'
              label={t('settings.minimal_order_value')}
              error={!!errors.orderMinimalValue}
              helperText={
                errors.orderMinimalValue ? t(String(errors.orderMinimalValue.message)) : ''
              }
            />
          </FieldsRow>
          <FieldsRow>
            <FormTextField
              name='emailForOrderNotifications'
              label={t('settings.email_for_order_notifications')}
              placeholder='office@example.com, accounting@example.com'
              width='556px'
              error={!!errors.emailForOrderNotifications}
              helperText={
                errors.emailForOrderNotifications
                  ? t(String(errors.emailForOrderNotifications.message))
                  : ''
              }
            />
          </FieldsRow>
          <Stack
            direction='column'
            sx={{
              mt: 1,
              width: 'fit-content',
            }}
          >
            <FormCheckbox name='useWholeNumbers' label={t('settings.only_whole_numbers')} />
            <FormCheckbox name='isMultiOrders' label={t('settings.multiple_orders_allowed')} />
            <FormCheckbox name='allowZeroOrders' label={t('settings.allow_zero_orders')} />
            <FormCheckbox
              name='showTotalColumn'
              label={t('settings.show_total_column_delivery_note')}
            />
            <FormCheckbox
              name='config.isEditUsualOrdersInBakery2b'
              label={t('settings.edit_wawi_orders')}
            />
          </Stack>
        </FieldsSection>
        <Stack
          direction='row'
          sx={{
            gap: '40px',
          }}
        >
          <FieldsSection title={t('settings.company_profile')} minWidth='270px'>
            <FieldsRow>
              <FormTextField name='brandName' label={t('settings.brand')} width='270px' />
            </FieldsRow>
            <FieldsRow>
              <FormTextField name='site' label={t('settings.site')} width='270px' />
            </FieldsRow>
          </FieldsSection>
          <FieldsSection title={t('settings.profile_photo')}>
            <FormImagePicker
              name='profileLogo'
              maxImgHeight={200}
              maxImgWidth={200}
              width={200}
              height={200}
              sx={{
                borderRadius: '50%',
                border: '1px solid var(--input-outlined-enabled-border, rgba(0, 0, 0, 0.23))',
              }}
              onUpdate={() => bakery2bSettingsTabState.pub.saveData()}
              onDelete={() => bakery2bSettingsTabState.pub.deleteProfileLogo()}
            />
          </FieldsSection>
        </Stack>
        <Stack
          direction='row'
          sx={{
            gap: '40px',
          }}
        >
          <FieldsSection title={t('settings.company_style')} minWidth='270px'>
            <FormSelect
              name='menuBgColor'
              options={menuBgColorOptions}
              label={t('settings.menu_navigation_color')}
              width='270px'
              disableClearable
            />
          </FieldsSection>
          <FieldsSection title={t('settings.background')}>
            <FormImagePicker
              name='menuBgImg'
              maxImgHeight={800}
              maxImgWidth={1600}
              onUpdate={() => bakery2bSettingsTabState.pub.saveData()}
              onDelete={() => bakery2bSettingsTabState.pub.deleteMenuBgImg()}
            />
          </FieldsSection>
        </Stack>
      </form>
    </FormProvider>
  );
};

export interface ICompanySettings {
  companyName: string;
  mainCurrency: string;
  cultureName: string;
  useWholeNumbers: boolean;
  orderFutureDays: number;
  isMultiOrders: boolean;
  orderMinimalValue: number;
  allowZeroOrders: boolean;
  showTotalColumn: boolean;
  emailForOrderNotifications?: string | null;
  brandName?: string | null;
  site?: string | null;
  profileLogo?: string | null;
  menuBgColor?: string | null;
  menuBgImg?: string | null;
  config?: {
    isEditUsualOrdersInBakery2b?: boolean;
  };
}

interface ISettingsTab {
  params: ICompanySettings;
}

const setByPath = (path: string, value: unknown) => {
  return path.split('.').reduceRight((acc, key) => ({ [key]: acc }), value);
};

const getSingleChangedPath = (obj: Record<string, any>, prefix = ''): string | null => {
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    const nextPath = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return getSingleChangedPath(value, nextPath);
    }

    return nextPath;
  }

  return null;
};

const getValueByPath = (obj: Record<string, any>, path: string) => {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
};
